'use client';

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { savePlaybackProgress } from '@/lib/supabase/playlists';

/**
 * ListenQueueContext (exported as AudioContext for backward compat during
 * the Phase 2–7 migration).
 *
 *   - Real MP3 playback via `audio_url`
 *   - localStorage queue: `aviado_listen_queue_v1` (migrated from legacy
 *     `aviado-audio-queue` on first mount; old key cleared after migration)
 *   - Cross-tab sync via `storage` event
 *   - `moveInQueue(id, delta)` for dock reorder controls
 *   - Playback-progress hook retained for signed-in users (see `playlists.ts`)
 *
 * Callers still import `{ AudioProvider, useAudio, QueueTrack }` — the
 * filename stays as AudioContext.tsx until Phase 7 cleanup.
 */

export type QueueTrack = {
  id: string;
  slug: string;
  title: string;
  supplement: string;
  audioUrl: string;
  evidenceTier?: string;
  durationSeconds?: number;
};

type ListenQueueContextType = {
  // Playback state
  currentTrack: QueueTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackSpeed: number;

  // Queue
  queue: QueueTrack[];
  queueIndex: number;

  // Actions
  playTrack: (track: QueueTrack) => void;
  togglePlayPause: () => void;
  seek: (time: number) => void;
  cycleSpeed: () => void;
  skipNext: () => void;
  skipPrev: () => void;
  addToQueue: (track: QueueTrack) => void;
  removeFromQueue: (trackId: string) => void;
  /** Reorder a track in the queue. `delta` is -1 (up) or +1 (down). */
  moveInQueue: (trackId: string, delta: -1 | 1) => void;
  clearQueue: () => void;
  isInQueue: (trackId: string) => boolean;

  // Player bar visibility
  showPlayerBar: boolean;
  dismissPlayerBar: () => void;
};

const AudioCtx = createContext<ListenQueueContextType | null>(null);

const QUEUE_STORAGE_KEY = 'aviado_listen_queue_v1';
const LEGACY_QUEUE_STORAGE_KEY = 'aviado-audio-queue';
const SPEEDS = [1, 1.25, 1.5, 2];

function loadQueue(): QueueTrack[] {
  if (typeof window === 'undefined') return [];
  try {
    // Migration: if new key is empty but legacy key has data, adopt it.
    const newRaw = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (newRaw) {
      return JSON.parse(newRaw);
    }
    const legacyRaw = localStorage.getItem(LEGACY_QUEUE_STORAGE_KEY);
    if (legacyRaw) {
      try {
        const parsed = JSON.parse(legacyRaw) as QueueTrack[];
        localStorage.setItem(QUEUE_STORAGE_KEY, legacyRaw);
        localStorage.removeItem(LEGACY_QUEUE_STORAGE_KEY);
        return parsed;
      } catch {
        localStorage.removeItem(LEGACY_QUEUE_STORAGE_KEY);
      }
    }
    return [];
  } catch {
    return [];
  }
}

function saveQueue(queue: QueueTrack[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // localStorage may be full or unavailable
  }
}

export function AudioProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTrack, setCurrentTrack] = useState<QueueTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [queue, setQueue] = useState<QueueTrack[]>([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const [showPlayerBar, setShowPlayerBar] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Load queue from localStorage on mount (+ migrate legacy key once)
  useEffect(() => {
    const saved = loadQueue();
    if (saved.length > 0) {
      setQueue(saved);
    }
    setInitialized(true);
  }, []);

  // Save queue to localStorage when it changes
  useEffect(() => {
    if (initialized) {
      saveQueue(queue);
    }
  }, [queue, initialized]);

  // Cross-tab sync: if another tab updates the queue key, adopt it.
  // Guard against malformed JSON (non-array, null, string) — the queue
  // state must remain an array so downstream `.length` / `.map` stay safe.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== QUEUE_STORAGE_KEY) return;
      if (!e.newValue) {
        setQueue([]);
        return;
      }
      try {
        const parsed = JSON.parse(e.newValue);
        if (!Array.isArray(parsed)) return;
        setQueue(parsed as QueueTrack[]);
      } catch {
        // Malformed JSON — keep in-memory queue intact.
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Periodically save playback progress for registered users (every 10s)
  useEffect(() => {
    if (!currentTrack || !isPlaying) return;
    const interval = setInterval(() => {
      const audio = audioRef.current;
      if (!audio || !currentTrack) return;
      savePlaybackProgress(
        currentTrack.id,
        audio.currentTime,
        false,
        audio.playbackRate,
      ).catch(() => {}); // Silently fail for unauthenticated users
    }, 10000);
    return () => clearInterval(interval);
  }, [currentTrack, isPlaying]);

  // Create/manage the audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    const audio = audioRef.current;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => {
      setIsPlaying(false);
      // Auto-advance to next track
      setQueueIndex((prev) => {
        const nextIdx = prev + 1;
        if (nextIdx < queue.length) {
          const nextTrack = queue[nextIdx];
          setCurrentTrack(nextTrack);
          audio.src = nextTrack.audioUrl;
          audio.play().then(() => setIsPlaying(true)).catch(() => {});
          return nextIdx;
        }
        return prev;
      });
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [queue]);

  const playTrack = useCallback((track: QueueTrack) => {
    const audio = audioRef.current;
    if (!audio) return;

    setCurrentTrack(track);
    setShowPlayerBar(true);
    audio.src = track.audioUrl;
    audio.playbackRate = playbackSpeed;
    audio.play().catch(() => {});

    // Add to queue if not already there
    setQueue((prev) => {
      const exists = prev.some((t) => t.id === track.id);
      if (exists) {
        const idx = prev.findIndex((t) => t.id === track.id);
        setQueueIndex(idx);
        return prev;
      }
      const updated = [...prev, track];
      setQueueIndex(updated.length - 1);
      return updated;
    });
  }, [playbackSpeed]);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
  }, [isPlaying, currentTrack]);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = time;
    setCurrentTime(time);
  }, []);

  const cycleSpeed = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const idx = SPEEDS.indexOf(playbackSpeed);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    audio.playbackRate = next;
    setPlaybackSpeed(next);
  }, [playbackSpeed]);

  const skipNext = useCallback(() => {
    if (queueIndex < queue.length - 1) {
      const nextTrack = queue[queueIndex + 1];
      playTrack(nextTrack);
      setQueueIndex(queueIndex + 1);
    }
  }, [queueIndex, queue, playTrack]);

  const skipPrev = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // If more than 3 seconds in, restart current track
    if (audio.currentTime > 3) {
      audio.currentTime = 0;
      setCurrentTime(0);
      return;
    }
    if (queueIndex > 0) {
      const prevTrack = queue[queueIndex - 1];
      playTrack(prevTrack);
      setQueueIndex(queueIndex - 1);
    }
  }, [queueIndex, queue, playTrack]);

  const addToQueue = useCallback((track: QueueTrack) => {
    setQueue((prev) => {
      if (prev.some((t) => t.id === track.id)) return prev;
      return [...prev, track];
    });
    setShowPlayerBar(true);
  }, []);

  const removeFromQueue = useCallback((trackId: string) => {
    // Functional updaters (not direct closure reads) so two rapid X-clicks
    // in the dock each compute against the latest queue state.
    setQueue((prevQueue) => {
      const idx = prevQueue.findIndex((t) => t.id === trackId);
      if (idx < 0) return prevQueue;
      const next = prevQueue.filter((t) => t.id !== trackId);

      setQueueIndex((prevIdx) => {
        if (idx < prevIdx) return prevIdx - 1;
        if (idx > prevIdx) return prevIdx;

        // Removing the currently-playing track. Advance to whatever now
        // occupies this slot, or fully tear down (mirroring clearQueue) if
        // the queue empties out.
        const audio = audioRef.current;
        if (next.length === 0) {
          if (audio) {
            audio.pause();
            audio.src = '';
          }
          setCurrentTrack(null);
          setIsPlaying(false);
          setCurrentTime(0);
          setDuration(0);
          setShowPlayerBar(false);
          return -1;
        }
        const newIdx = Math.min(prevIdx, next.length - 1);
        const nextTrack = next[newIdx];
        setCurrentTrack(nextTrack);
        if (audio) {
          audio.src = nextTrack.audioUrl;
          audio.playbackRate = playbackSpeed;
          audio.play().catch(() => {});
        }
        return newIdx;
      });
      return next;
    });
  }, [playbackSpeed]);

  const moveInQueue = useCallback((trackId: string, delta: -1 | 1) => {
    setQueue((prev) => {
      const i = prev.findIndex((t) => t.id === trackId);
      if (i < 0) return prev;
      const j = i + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      // Keep queueIndex pointing at the same track if it moved.
      setQueueIndex((qi) => {
        if (qi === i) return j;
        if (qi === j) return i;
        return qi;
      });
      return next;
    });
  }, []);

  const clearQueue = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    setQueue([]);
    setQueueIndex(-1);
    setCurrentTrack(null);
    setIsPlaying(false);
    setShowPlayerBar(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const isInQueue = useCallback((trackId: string) => {
    return queue.some((t) => t.id === trackId);
  }, [queue]);

  const dismissPlayerBar = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
    setShowPlayerBar(false);
    setIsPlaying(false);
  }, []);

  return (
    <AudioCtx.Provider
      value={{
        currentTrack,
        isPlaying,
        currentTime,
        duration,
        playbackSpeed,
        queue,
        queueIndex,
        playTrack,
        togglePlayPause,
        seek,
        cycleSpeed,
        skipNext,
        skipPrev,
        addToQueue,
        removeFromQueue,
        moveInQueue,
        clearQueue,
        isInQueue,
        showPlayerBar,
        dismissPlayerBar,
      }}
    >
      {children}
    </AudioCtx.Provider>
  );
}

export function useAudio() {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error('useAudio must be used within AudioProvider');
  return ctx;
}

/** Alias — new canonical name, same hook. Prefer this in new code. */
export const useListenQueue = useAudio;
